/**
 * React Router Configuration
 * @implements TS-032 - Customer Portal Frontend Package Setup
 * @implements TS-016 - Product Owner Registration UI
 * @implements TS-017 - Mock Project Redirect

 * @implements US-008b - Password Setup UI
 * @implements US-013 - Project Creation (Frontend)
 * @implements US-014 - Project Listing & Navigation
 * @implements US-015 - Project Editing
 * @implements US-029 - Basic Specification Viewing
 * @implements US-037 - Basic Roadmap Viewing

 *
 * Defines the application routes.
 */

import { createBrowserRouter, RouteObject } from 'react-router-dom';
import {
  HomePage,
  LoginPage,
  RegisterPage,
  RegisterProductOwnerPage,
  DashboardPOPage,
  CreateProjectPage,
  ProjectListPage,
  ProjectViewPage,
  ProjectSettingsPage,
  AccountSettingsPage,
  SpecificationListPage,
  SpecificationDetailPage,
  RoadmapPage,
  AuthErrorPage,
} from '@/pages';
import { ProtectedRoute } from '@/components/auth';

/**
 * Route definitions
 * @implements TS-016 - Added /register/product-owner route
 * @implements TS-017 - Added /onboarding/create-project route
 * @implements TS-022 - Added protected dashboard routes
 */
const routes: RouteObject[] = [
  // Public routes
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/register/product-owner',
    element: <RegisterProductOwnerPage />,
  },
  // @implements BS-001.3 - OAuth Provider Lock-In error page
  {
    path: '/auth/error',
    element: <AuthErrorPage />,
  },
  // Protected dashboard routes
  // @implements TS-022.4 - Protected routes with role requirements
  {
    path: '/dashboard/po',
    element: (
      <ProtectedRoute requiredRole="PRODUCT_OWNER">
        <DashboardPOPage />
      </ProtectedRoute>
    ),
  },

  // Onboarding routes
  // @implements TS-017.2 - Create project placeholder route
  // @implements US-013 - Project Creation (Frontend)
  {
    path: '/onboarding/create-project',
    element: (
      <ProtectedRoute requiredRole="PRODUCT_OWNER">
        <CreateProjectPage />
      </ProtectedRoute>
    ),
  },

  // Project routes
  // @implements US-014 - Project Listing & Navigation
  // @implements FS-002.3.1 - View All Projects
  {
    path: '/projects',
    element: (
      <ProtectedRoute requiredRole="PRODUCT_OWNER">
        <ProjectListPage />
      </ProtectedRoute>
    ),
  },
  // @implements US-013 - Project Creation (Frontend)
  // @implements FS-002.1.1 - Create New Project
  {
    path: '/projects/new',
    element: (
      <ProtectedRoute requiredRole="PRODUCT_OWNER">
        <CreateProjectPage />
      </ProtectedRoute>
    ),
  },
  // @implements US-014 - Project Listing & Navigation
  // @implements FS-002.3.2 - Project Navigation
  // @implements FS-002.7.1 - Automatic Conversation Initiation (placeholder)
  {
    path: '/projects/:id',
    element: (
      <ProtectedRoute requiredRole="PRODUCT_OWNER">
        <ProjectViewPage />
      </ProtectedRoute>
    ),
  },
  // @implements US-015 - Project Editing
  // @implements FS-002.2.1 - Edit Project Details
  {
    path: '/projects/:id/settings',
    element: (
      <ProtectedRoute requiredRole="PRODUCT_OWNER">
        <ProjectSettingsPage />
      </ProtectedRoute>
    ),
  },

  // Specification routes
  // @implements US-029 - Basic Specification Viewing
  // @implements FS-004.5.1 - View Specification List
  {
    path: '/projects/:projectId/specifications',
    element: (
      <ProtectedRoute requiredRole="PRODUCT_OWNER">
        <SpecificationListPage />
      </ProtectedRoute>
    ),
  },
  // @implements US-029 - Basic Specification Viewing
  // @implements FS-004.5.2 - View Specification Details
  {
    path: '/projects/:projectId/specifications/:specId',
    element: (
      <ProtectedRoute requiredRole="PRODUCT_OWNER">
        <SpecificationDetailPage />
      </ProtectedRoute>
    ),
  },

  // Roadmap routes
  // @implements US-037 - Basic Roadmap Viewing
  // @implements FS-005.6.1 - View Roadmap
  {
    path: '/projects/:projectId/roadmap',
    element: (
      <ProtectedRoute requiredRole="PRODUCT_OWNER">
        <RoadmapPage />
      </ProtectedRoute>
    ),
  },

  // Account settings route
  // @implements US-008b - Password Setup UI
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <AccountSettingsPage />
      </ProtectedRoute>
    ),
  },
];

/**
 * Router instance
 */
export const router = createBrowserRouter(routes);
